import { useNavigate, useSearch } from '@tanstack/react-router';
import { AnimatePresence, motion } from 'framer-motion';
import { X } from 'lucide-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useTabsStore } from '@shared/stores/tabs.store';
import { useUIStore } from '@shared/stores/ui.store';

import { ChatHistory } from './components/ChatHistory';
import { ChatThread } from './components/ChatThread';
import { Composer } from './components/Composer';
import { DataPolicyDialogHost } from './components/DataPolicyDialog';
import { EmptyState } from './components/EmptyState';
import { TokenMeter } from './components/TokenMeter';
import { usePickerPolicy } from './data-policy/picker-policy';
import { useChat } from './hooks/use-chat';
import { NEW_CHAT_DRAFT_KEY } from './store/composer-attachments.store';
import { conversationById, useConversationsStore } from './store/conversations.store';
import { isChatOpenInOtherTab } from './tab-binding';

export function AiCioPage() {
  const { t, i18n } = useTranslation('ai-cio');
  const isRtl = i18n.dir() === 'rtl';
  const {
    conversation,
    isEmpty,
    historyState,
    greetingName,
    greeting,
    modelId,
    modelName,
    sessionModelUnknown,
    connected,
    thinking,
    setThinking,
    agentic,
    setAgentic,
    waitingForPolicy,
    chatHasInvestorData,
    send,
    setModelId,
    setFeedback,
    regenerate,
    answerWithInvestbanqModel,
    answerInteraction,
    decideProposal,
    busy,
    stopping,
    following,
    compacting,
    meter,
    compactedThroughTurn,
    compactionNote,
    compactOff,
    compactHidden,
    compact,
    dismissCompactionNote,
    stop,
    removeQueued,
    takeBackQueued,
    sendQueuedNow,
    startNewChatWith,
  } = useChat();
  const historyOpen = useUIStore((s) => s.aiCioPageHistoryOpen);
  const setHistoryOpen = useUIStore((s) => s.setAiCioPageHistoryOpen);
  const setTabLabel = useTabsStore((s) => s.setTabLabel);

  const navigate = useNavigate();
  const search = useSearch({ strict: false });
  const urlChatId = search.c;
  // Turns false when the chat in the URL is deleted (or cleared), so the binding
  // below re-runs and moves this tab to a fresh chat.
  const urlChatKnown = useConversationsStore((s) =>
    urlChatId ? conversationById(s.conversations, urlChatId) !== undefined : false,
  );

  // Bind this tab to the chat in the URL (?c=); create/stamp one when missing.
  // Reads store state directly so streaming updates don't re-run this.
  useEffect(() => {
    const store = useConversationsStore.getState();
    if (urlChatId) {
      if (conversationById(store.conversations, urlChatId)) {
        if (store.activeId !== urlChatId) store.selectConversation(urlChatId);
        return;
      }
      // Unknown/stale id — fall back to a fresh chat and correct the URL.
      const id = store.newChat(undefined, { forceNew: true });
      void navigate({ to: '/ai-cio', search: { c: id }, replace: true });
      return;
    }
    // Resume the active chat only when no other tab owns it (DEV-3519).
    const activeId = store.activeId;
    const canResume =
      activeId != null &&
      conversationById(store.conversations, activeId) &&
      !isChatOpenInOtherTab(activeId);
    const id = canResume ? activeId : store.newChat(undefined, { forceNew: true });
    void navigate({ to: '/ai-cio', search: { c: id }, replace: true });
  }, [urlChatId, urlChatKnown, navigate]);

  // Keep the tab label in sync with this chat's title (falls back to "New chat").
  useEffect(() => {
    if (!urlChatId || !conversation || conversation.id !== urlChatId) return;
    setTabLabel(`/ai-cio?c=${urlChatId}`, conversation.title || t('history.new', 'New chat'));
  }, [urlChatId, conversation, setTabLabel, t]);

  // Close the recent-chats palette on unmount so it doesn't leak to the panel.
  useEffect(
    () => () => {
      setHistoryOpen(false);
    },
    [setHistoryOpen],
  );

  const openNewChatWith = (id: string) => {
    const c = startNewChatWith(id);
    setHistoryOpen(false);
    void navigate({ to: '/ai-cio', search: { c } });
  };
  const pickerPolicy = usePickerPolicy({ chatHasInvestorData, onStartNewChat: openNewChatWith });

  return (
    <div className="relative flex h-full flex-col items-center overflow-hidden">
      <DataPolicyDialogHost />
      {isEmpty || !conversation ? (
        // ── Empty state: centered greeting + composer ──────────────────────
        <div className="flex h-full w-full items-center justify-center p-4">
          <EmptyState
            name={greetingName}
            greeting={greeting}
            modelId={modelId}
            onModelChange={setModelId}
            onSend={send}
            draftKey={conversation?.id ?? NEW_CHAT_DRAFT_KEY}
            thinking={thinking}
            onThinkingChange={setThinking}
            agentic={agentic}
            onAgenticChange={setAgentic}
            modelName={modelName}
            policy={pickerPolicy}
          />
        </div>
      ) : (
        // ── Active chat: scrolling thread + sticky composer ────────────────
        <div className="flex h-full w-full max-w-[738px] flex-col px-4">
          {conversation.messages.length === 0 ? (
            <div className="min-h-0 flex-1 py-6">
              <p
                className="text-sm text-muted-foreground"
                role={historyState === 'error' ? 'alert' : 'status'}
              >
                {historyState === 'error'
                  ? t('history.loadFailed', "Couldn't load this conversation's history.")
                  : t('history.loading', 'Loading conversation…')}
              </p>
            </div>
          ) : (
            <ChatThread
              conversation={conversation}
              onFeedback={setFeedback}
              onRegenerate={regenerate}
              onAnswerInteraction={answerInteraction}
              onDecideProposal={decideProposal}
              busy={busy}
              following={following}
              stopping={stopping}
              compactedThroughTurn={compactedThroughTurn}
              onRemoveQueued={removeQueued}
              onSendQueuedNow={sendQueuedNow}
              onAnswerInternal={answerWithInvestbanqModel}
              onAskInNewChat={() => {
                openNewChatWith(modelId);
              }}
              className="min-h-0 flex-1 py-6"
            />
          )}
          <div className="shrink-0 pb-4">
            <Composer
              modelId={modelId}
              sessionModelUnknown={sessionModelUnknown}
              connected={connected}
              onModelChange={setModelId}
              onSend={send}
              draftKey={conversation.id}
              thinking={thinking}
              onThinkingChange={setThinking}
              agentic={agentic}
              onAgenticChange={setAgentic}
              conversationId={conversation.id}
              modelName={modelName}
              busy={busy}
              stopping={stopping}
              compacting={compacting}
              meter={
                meter ? (
                  <TokenMeter
                    figure={meter}
                    busy={busy}
                    compacting={compacting}
                    off={compactOff}
                    withoutControl={connected || compactHidden}
                    onCompact={compact}
                  />
                ) : undefined
              }
              compactionNote={compactionNote}
              onCompactionNoteDismiss={dismissCompactionNote}
              onStop={stop}
              onTakeBack={takeBackQueued}
              policy={pickerPolicy}
              waitingForPolicy={waitingForPolicy}
            />
          </div>
        </div>
      )}

      {/* ── Recent-chats history drawer (inline-end side, per spec) ─────────── */}
      <AnimatePresence>
        {historyOpen && (
          <>
            {/* Click-catcher: closes on outside click (no dimming, per design). */}
            <button
              type="button"
              aria-label={t('panel.close', 'Close')}
              className="absolute inset-0 z-40 cursor-default"
              onClick={() => {
                setHistoryOpen(false);
              }}
            />
            <motion.aside
              initial={{ x: isRtl ? '-100%' : '100%' }}
              animate={{ x: 0 }}
              exit={{ x: isRtl ? '-100%' : '100%' }}
              transition={{ type: 'spring', stiffness: 320, damping: 34 }}
              className="absolute inset-y-0 end-0 z-50 flex w-[min(420px,100%)] flex-col border-s border-t bg-background shadow-xl"
            >
              <div className="flex h-14 shrink-0 items-center justify-between border-b px-4">
                <p className="text-sm font-semibold">{t('panel.history', 'History')}</p>
                <button
                  type="button"
                  aria-label={t('panel.close', 'Close')}
                  title={t('panel.close', 'Close')}
                  onClick={() => {
                    setHistoryOpen(false);
                  }}
                  className="flex size-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                >
                  <X className="size-4" />
                </button>
              </div>
              <ChatHistory
                showSearch
                className="min-h-0 flex-1"
                onSelect={(id) => {
                  setHistoryOpen(false);
                  // Bind the tab to the picked chat via the URL.
                  void navigate({ to: '/ai-cio', search: { c: id } });
                }}
              />
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
